import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { posthog } from '../../lib/posthog.client';
import { Modal, Spin } from 'antd';
import execute from '../../hooks/execute';
import { protectedResources } from '../../config/msalConfig';
import { useUser } from '../../components/UserContext';
import TitleComponent from '../../components/Common/TitleComponent';
// Session expiry is handled by execute.jsx, no need to import here

function Report() {
    const { reportId, moduleShortName } = useParams();
    const { userDetails } = useUser();
    const navigate = useNavigate();

    // State for token management
    const [embedUrl, setEmbedUrl] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [isEmbedToken, setIsEmbedToken] = useState(undefined);
    const [tokenExpiration, setTokenExpiration] = useState(null); // Track token expiration time
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true); // Track if we need to fetch initial data
    const [isCapacityPaused, setIsCapacityPaused] = useState(false); // Hide the embed (and its own error dialog) when capacity is paused
    const capacityModalShownRef = useRef(false); // Prevent stacking multiple capacity modals
    const reportRef = useRef(null);
    const embedContainerRef = useRef(null);
    const retryCountRef = useRef(0);
    const refreshTimerRef = useRef(null); // Timer for proactive token refresh
    const isRefreshingRef = useRef(false); // Synchronous concurrency control to prevent race conditions
    const MAX_RETRY_ATTEMPTS = 2;
    const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1000; // Refresh 10 minutes before expiration

    const resolveBestDisplayOption = async (embeddedReport) => {
        const container = embedContainerRef.current;
        if (!container) {
            return models.DisplayOption.FitToPage;
        }

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        if (!containerWidth || !containerHeight) {
            return models.DisplayOption.FitToPage;
        }

        try {
            const activePage = await embeddedReport.getActivePage();
            const pageWidth = activePage?.defaultSize?.width;
            const pageHeight = activePage?.defaultSize?.height;

            if (!pageWidth || !pageHeight) {
                return models.DisplayOption.FitToPage;
            }

            const requiredHeightForFitWidth = containerWidth * (pageHeight / pageWidth);

            // Use width when it can fit fully in height, otherwise fall back to full-page fit.
            if (requiredHeightForFitWidth <= containerHeight) {
                return models.DisplayOption.FitToWidth;
            }
        } catch (sizeError) {
            console.warn('[PowerBI] Could not resolve active page size; using FitToPage', sizeError);
        }

        return models.DisplayOption.FitToPage;
    };

    const applyFitToContainerLayout = async (embeddedReport) => {
        const reportInstance = embeddedReport || reportRef.current;
        if (!reportInstance) return;

        const bestDisplayOption = await resolveBestDisplayOption(reportInstance);

        try {
            await reportInstance.updateSettings({
                layoutType: models.LayoutType.Custom,
                customLayout: {
                    displayOption: bestDisplayOption
                }
            });
        } catch (layoutError) {
            console.error('[PowerBI] Failed to apply adaptive layout:', layoutError);
        }
    };

    /**
     * Check if user has access to the requested module
     * Returns: true (has access), false (no access), undefined (still loading)
     */
    const hasModuleAccess = () => {
        if (!userDetails || !userDetails.Modules) {
            return undefined; // Still loading user details
        }
        // Check if the moduleShortName from URL matches any of user's assigned modules
        return userDetails.Modules.some(module => module.ShortName === moduleShortName);
    };

    // Redirect to dashboard if user doesn't have access to the module
    useEffect(() => {
        const access = hasModuleAccess();
        if (access === false) {
            console.log(
                "Redirecting to dashboard - user does not have access to module:",
                moduleShortName,
                "User modules:",
                userDetails?.Modules?.map(m => m.ShortName)
            );
            window.location.href = "/dashboard";
        }
    }, [userDetails, moduleShortName, navigate]);

    /**
     * Fetch initial report data when location.state is missing
     * This handles page refresh and direct URL access scenarios
     */
    const fetchInitialReportData = async () => {
        const fetchStartTime = Date.now();

        try {
            setIsInitializing(true);

            console.log('┌─────────────────────────────────────────────────────────────┐');
            console.log('│ [PowerBI Init] Fetching Initial Report Data                │');
            console.log('└─────────────────────────────────────────────────────────────┘');
            console.log('[PowerBI Init] ReportID:', reportId);
            console.log('[PowerBI Init] Step 1: Calling /pbi/refresh-token...');

            const response = await execute(
                'POST',
                `${protectedResources.api.baseEndPoint}/pbi/refresh-token`,
                { reportId }
            );

            const apiDuration = Date.now() - fetchStartTime;
            console.log(`[PowerBI Init] Step 1: API call completed (${apiDuration}ms)`);

            if (!response) {
                throw new Error('Failed to fetch embed token: No response from server');
            }

            if (response.success === false) {
                const errorMsg = response.message || 'Failed to generate embed token';
                throw new Error(errorMsg);
            }

            if (!response.success || !response.data) {
                throw new Error('Failed to fetch embed token: Invalid response format');
            }

            console.log('[PowerBI Init] Step 2: Validating token response...');

            console.log('[PowerBI Init] ✓ Token response received:', {
                reportId,
                embedUrl: response.data.embedUrl,
                hasToken: !!response.data.token,
                tokenType: response.data.isEmbedToken ? 'Embed' : 'AAD',
                tokenExpiration: response.data.tokenExpiration
            });

            // Calculate time until expiration
            if (response.data.tokenExpiration) {
                const now = new Date();
                const expiryDate = new Date(response.data.tokenExpiration);
                const minutesUntilExpiry = Math.round((expiryDate - now) / 1000 / 60);
                console.log(`[PowerBI Init] Token valid for: ${minutesUntilExpiry} minutes (expires at ${expiryDate.toLocaleTimeString()})`);
            }

            console.log('[PowerBI Init] Step 3: Updating React state...');

            // Update state with token data
            setAccessToken(response.data.token);
            setEmbedUrl(response.data.embedUrl);
            setIsEmbedToken(response.data.isEmbedToken);
            setTokenExpiration(response.data.tokenExpiration); // Store token expiration time

            const totalDuration = Date.now() - fetchStartTime;
            console.log('[PowerBI Init] ────────────────────────────────────────────');
            console.log(`[PowerBI Init] ✓ Initial data fetch SUCCESS (${totalDuration}ms)`);
            console.log('[PowerBI Init] Report will now embed');
            console.log('[PowerBI Init] ────────────────────────────────────────────');

            return response.data;

        } catch (error) {
            const totalDuration = Date.now() - fetchStartTime;

            console.error('┌─────────────────────────────────────────────────────────────┐');
            console.error('│ [PowerBI Init] ✗ Initial Data Fetch FAILED                 │');
            console.error('└─────────────────────────────────────────────────────────────┘');
            console.error(`[PowerBI Init] Duration: ${totalDuration}ms`);
            console.error('[PowerBI Init] Error:', error);
            console.error('[PowerBI Init] Error message:', error.message);
            console.error('[PowerBI Init] Stack:', error.stack);

            // Check if this is a session expiry (handled by execute.jsx)
            if (error.status === 401 || error.status === 403) {
                console.warn('[PowerBI Init] Session expired - user will be redirected to login');
                // Session expiry modal already shown by execute.jsx
                return;
            }

            // PowerBI capacity paused/unavailable - show the single friendly banner
            if (error.status === 503 || error.errorType === 'CAPACITY_UNAVAILABLE') {
                showCapacityUnavailableModal();
                return;
            }

            // Show error modal for other errors
            // PATCH: Wrap Modal in try-catch to prevent unhandled rejections
            try {
                Modal.error({
                    title: 'Failed to Load Report',
                    content: error.message || 'Unable to load the report. Please try again or contact support.',
                    okText: 'OK'
                });
            } catch (modalError) {
                console.error('[PowerBI Init] Modal display failed:', modalError);
                // Fallback: Use native alert if Modal fails
                alert('Failed to Load Report\n\n' + (error.message || 'Unable to load the report. Please try again or contact support.'));
            }

            throw error;
        } finally {
            setIsInitializing(false);
        }
    };

    /**
     * Show the single user-friendly "Reporting Service Unavailable" banner.
     * Also unmounts the PowerBI embed so the SDK's own error dialog
     * ("Cannot load model") never appears behind the banner.
     * OK redirects the user to the dashboard.
     */
    const showCapacityUnavailableModal = () => {
        // Unmount the embed iframe so the PowerBI SDK error dialog is removed
        setIsCapacityPaused(true);

        // Only show the banner once even if multiple errors fire
        if (capacityModalShownRef.current) return;
        capacityModalShownRef.current = true;

        const goToDashboard = () => {
            window.location.href = '/dashboard';
        };

        try {
            Modal.warning({
                title: 'Reporting Service Unavailable',
                centered: true,
                width: 600,
                keyboard: false,
                content: (
                    <div style={{ fontSize: '16px', lineHeight: 1.6 }}>
                        <p>The Reporting service is unavailable or is currently in maintenance.</p>
                        <p style={{ marginTop: '12px' }}>
                            Please contact supportportal@thebicollab.com to report this issue.
                        </p>
                    </div>
                ),
                okText: 'OK',
                okButtonProps: { size: 'large' },
                onOk: goToDashboard
            });
        } catch (modalError) {
            console.error('[PowerBI] Modal display failed:', modalError);
            // Fallback: Use native alert if Modal fails
            alert('Reporting Service Unavailable\n\nThe Reporting service is unavailable or is currently in maintenance.\n\nPlease contact supportportal@thebicollab.com to report this issue.');
            goToDashboard();
        }
    };

    /**
     * Schedule proactive token refresh based on token expiration time
     * Microsoft Recommended Pattern for Embed Tokens (non-SaaS scenarios)
     * - Checks every 30 seconds
     * - Refreshes 10 minutes before expiration
     * - Uses setAccessToken() to update token without page reload
     */
    const scheduleTokenRefresh = () => {
        // Clear any existing timer
        if (refreshTimerRef.current) {
            clearInterval(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }

        if (!tokenExpiration || !reportRef.current) {
            console.log('[PowerBI Scheduler] ⏸️  Cannot schedule - missing data:', {
                hasTokenExpiration: !!tokenExpiration,
                hasReportRef: !!reportRef.current
            });
            return;
        }

        console.log('┌─────────────────────────────────────────────────────────────┐');
        console.log('│ [PowerBI Scheduler] ▶️  Token Refresh Scheduler STARTING    │');
        console.log('└─────────────────────────────────────────────────────────────┘');
        console.log('[PowerBI Scheduler] Token expires at:', tokenExpiration);

        const expiryDate = new Date(tokenExpiration);
        const now = new Date();
        const minutesUntilExpiry = Math.round((expiryDate - now) / 1000 / 60);
        console.log(`[PowerBI Scheduler] Time until expiration: ${minutesUntilExpiry} minutes`);
        console.log('[PowerBI Scheduler] Will refresh 10 minutes before expiry');
        console.log('[PowerBI Scheduler] Checking every 30 seconds...');

        // Microsoft recommendation: Check every 30 seconds
        refreshTimerRef.current = setInterval(async () => {
            const now = new Date();
            const expiryDate = new Date(tokenExpiration);
            const timeUntilExpiry = expiryDate.getTime() - now.getTime();
            const minutesUntilExpiry = Math.round(timeUntilExpiry / 1000 / 60);

            // Log heartbeat every 5 minutes
            if (minutesUntilExpiry % 5 === 0) {
                console.log(`[PowerBI Scheduler] ⏱️  Heartbeat: ${minutesUntilExpiry} minutes until refresh (expires at ${expiryDate.toLocaleTimeString()})`);
            }

            // Microsoft recommendation: Refresh 10 minutes before expiration
            if (timeUntilExpiry <= REFRESH_BEFORE_EXPIRY_MS && timeUntilExpiry > 0) {
                console.log('┌─────────────────────────────────────────────────────────────┐');
                console.log('│ [PowerBI Scheduler] 🔄 PROACTIVE REFRESH TRIGGERED          │');
                console.log('└─────────────────────────────────────────────────────────────┘');
                console.log(`[PowerBI Scheduler] Reason: ${minutesUntilExpiry} minutes until expiry (threshold: 10 min)`);
                console.log(`[PowerBI Scheduler] Current time: ${now.toLocaleTimeString()}`);
                console.log(`[PowerBI Scheduler] Token expires: ${expiryDate.toLocaleTimeString()}`);

                // Clear the interval to prevent multiple refresh attempts
                if (refreshTimerRef.current) {
                    clearInterval(refreshTimerRef.current);
                    refreshTimerRef.current = null;
                }

                // PostHog: Proactive refresh triggered
                try {
                    posthog.capture('PBI-proactive-token-refresh-triggered', {
                        reportId,
                        minutesUntilExpiry,
                        scheduledRefreshTime: REFRESH_BEFORE_EXPIRY_MS / 1000 / 60
                    });
                } catch (posthogError) {
                    console.error('[PostHog] Capture failed (non-critical):', posthogError);
                }

                try {
                    await refreshPowerBIToken(true); // true = proactive refresh
                    console.log('[PowerBI Scheduler] ✓ Proactive token refresh completed successfully');

                    // After successful refresh, tokenExpiration state will be updated
                    // and useEffect will call scheduleTokenRefresh again
                } catch (error) {
                    console.error('[PowerBI Scheduler] ✗ Proactive token refresh failed');
                    console.error('[PowerBI Scheduler] Error:', error);
                    // Error already handled by refreshPowerBIToken
                }
            } else if (timeUntilExpiry <= 0) {
                console.warn('┌─────────────────────────────────────────────────────────────┐');
                console.warn('│ [PowerBI Scheduler] ⚠️  TOKEN ALREADY EXPIRED               │');
                console.warn('└─────────────────────────────────────────────────────────────┘');
                console.warn(`[PowerBI Scheduler] Token expired ${Math.abs(minutesUntilExpiry)} minutes ago`);
                console.warn(`[PowerBI Scheduler] Expired at: ${expiryDate.toLocaleTimeString()}`);

                // Clear the interval
                if (refreshTimerRef.current) {
                    clearInterval(refreshTimerRef.current);
                    refreshTimerRef.current = null;
                }
            }
        }, 30000); // Check every 30 seconds (Microsoft recommendation)

        console.log('[PowerBI Scheduler] ✓ Scheduler started successfully');
    };

    /**
     * Fetch fresh Power BI token from backend
     * Called when Power BI token expires or proactively before expiration
     * @param {boolean} isProactive - True if this is a proactive refresh, false if reactive (error-triggered)
     */
    const refreshPowerBIToken = async (isProactive = false) => {
        // CRITICAL FIX: Prevent concurrent refresh calls using synchronous ref check
        // React state updates are async/batched, causing race conditions
        if (isRefreshingRef.current) {
            console.log('[PowerBI] Token refresh already in progress (detected via ref), skipping');
            return;
        }

        // CRITICAL FIX: Prevent infinite retry loops
        if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
            console.error('[PowerBI] Max retry attempts reached, stopping refresh');

            // PATCH: Wrap Modal in try-catch to prevent unhandled rejections
            try {
                Modal.error({
                    title: 'Report Refresh Failed',
                    content: 'Unable to refresh the report after multiple attempts. Please navigate away and back to the report.',
                });
            } catch (modalError) {
                console.error('[PowerBI] Modal display failed:', modalError);
                // Fallback: Use native alert if Modal fails
                alert('Report Refresh Failed\n\nUnable to refresh the report after multiple attempts. Please navigate away and back to the report.');
            }
            return;
        }

        try {
            // Set ref FIRST (synchronous) to prevent race conditions
            isRefreshingRef.current = true;
            // Then set state for UI updates
            setIsRefreshing(true);
            retryCountRef.current += 1;
            console.log('[PowerBI] Refreshing token for report:', reportId, `(attempt ${retryCountRef.current}/${MAX_RETRY_ATTEMPTS})`);

            // PostHog: Token refresh started
            try {
                posthog.capture('PBI-token-refresh-started', {
                    reportId,
                    attempt: retryCountRef.current,
                    maxAttempts: MAX_RETRY_ATTEMPTS,
                    isProactive // Track if this was proactive or reactive refresh
                });
            } catch (posthogError) {
                console.error('[PostHog] Capture failed (non-critical):', posthogError);
            }

            const response = await execute(
                'POST',
                `${protectedResources.api.baseEndPoint}/pbi/refresh-token`,
                { reportId }
            );

            // CRITICAL FIX: Check response structure properly
            // Backend can return {success: false, message: "..."} or {success: true, data: {...}}
            if (!response) {
                throw new Error('Failed to refresh token: No response from server');
            }

            if (response.success === false) {
                // Backend explicitly returned failure
                const errorMsg = response.message || 'Failed to generate embed token';
                console.error('[PowerBI] Backend returned failure:', errorMsg);
                throw new Error(errorMsg);
            }

            if (!response.success || !response.data) {
                // Unexpected response format
                console.error('[PowerBI] Unexpected response format:', response);
                throw new Error('Failed to refresh token: Invalid response format');
            }

            // Success case
            console.log('[PowerBI] Token refreshed successfully');

            // PostHog: Token refresh succeeded
            try {
                posthog.capture('PBI-token-refresh-success', {
                    reportId,
                    attempt: retryCountRef.current,
                    duration: 'auto-calculated-by-posthog'
                });
            } catch (posthogError) {
                console.error('[PostHog] Capture failed (non-critical):', posthogError);
            }

            // Reset retry counter on success
            retryCountRef.current = 0;

            // Update state with new token
            setAccessToken(response.data.token);
            setEmbedUrl(response.data.embedUrl);
            setIsEmbedToken(response.data.isEmbedToken);
            setTokenExpiration(response.data.tokenExpiration); // Store token expiration time

            // Apply the new token to the embedded report (for reactive/fallback refresh only)
            if (reportRef.current) {
                try {
                    console.log('[PowerBI] Applying refreshed token to embedded report...');
                    await reportRef.current.setAccessToken(response.data.token);
                    console.log('[PowerBI] ✓ Token refresh applied successfully');

                    try {
                        posthog.capture('PBI-fallback-token-applied', {
                            reportId,
                            expiresAt: response.data.tokenExpiration
                        });
                    } catch (posthogError) {
                        console.error('[PostHog] Capture failed (non-critical):', posthogError);
                    }
                } catch (error) {
                    console.error('[PowerBI] ✗ Failed to apply token refresh:', error);
                }
            }

            return response.data;
        } catch (error) {
            console.error('[PowerBI] Token refresh failed:', error);

            // PostHog: Token refresh failed
            try {
                posthog.capture('PBI-token-refresh-failed', {
                    reportId,
                    attempt: retryCountRef.current,
                    errorStatus: error.status || 'unknown',
                    errorMessage: error.message || 'Unknown error',
                    isSessionExpiry: error.status === 401 || error.status === 403
                });
            } catch (posthogError) {
                console.error('[PostHog] Capture failed (non-critical):', posthogError);
            }

            // If backend returns 401/403, it's a session expiry
            if (error.status === 401 || error.status === 403) {
                console.log('[PowerBI] Backend session expired during token refresh');
                // showSessionExpiredModal is already called by execute.jsx
            } else if (error.status === 503 || error.errorType === 'CAPACITY_UNAVAILABLE') {
                // PowerBI capacity is paused or unavailable
                console.error('[PowerBI] PowerBI capacity is unavailable (paused or service issue)');
                showCapacityUnavailableModal();
            } else {
                // Other errors - show user-friendly message with specific error
                const errorMessage = error.message || 'Unknown error occurred';
                const isMaxRetriesReached = retryCountRef.current >= MAX_RETRY_ATTEMPTS;

                // PATCH: Wrap Modal in try-catch to prevent unhandled rejections
                try {
                    Modal.error({
                        title: 'Report Refresh Failed',
                        content: isMaxRetriesReached
                            ? 'Unable to refresh the report after multiple attempts. Please try navigating away and back to the report, or contact support if the issue persists.'
                            : `Unable to refresh the report: ${errorMessage}. Please try again or navigate away and back to the report.`,
                        okText: 'OK'
                    });
                } catch (modalError) {
                    console.error('[PowerBI] Modal display failed:', modalError);
                    // Fallback: Use native alert if Modal fails
                    if (window.confirm('Report Refresh Failed\n\n' + (isMaxRetriesReached
                        ? 'Unable to refresh the report after multiple attempts. Please try navigating away and back to the report, or contact support if the issue persists.'
                        : `Unable to refresh the report: ${errorMessage}. Please try again or navigate away and back to the report.`) + '\n\nClick OK to acknowledge.')) {
                        // User acknowledged
                    }
                }
            }

            throw error;
        } finally {
            // Reset both ref and state to allow future refreshes
            isRefreshingRef.current = false;
            setIsRefreshing(false);
        }
    };

    /**
     * Check if Power BI error indicates token expiration
     * OPTIMIZED: More specific patterns to avoid false positives with capacity pause errors
     */
    const isPowerBITokenError = (event) => {
        const detail = event?.detail;
        const message = (detail?.message || '') + ' ' + (detail?.detailedMessage || '');
        const errorCode = detail?.errorCode || '';
        const messageLower = message.toLowerCase();
        const errorCodeLower = errorCode.toLowerCase();

        // SPECIFIC token error patterns (avoid generic patterns that match capacity errors)
        const tokenErrorPatterns = [
            'tokenexpired',
            'token expired',
            'token has expired',
            'access token',
            'bearer token',
            'authenticate',
            'authentication failed',
            'authentication required'
        ];

        // Check message patterns
        const messageMatch = tokenErrorPatterns.some(pattern =>
            messageLower.includes(pattern)
        );

        // Check error codes (be specific)
        const codeMatch = errorCodeLower === '401' ||
                         errorCodeLower === '403' ||
                         errorCodeLower === 'unauthorized';

        return messageMatch || codeMatch;
    };

    /**
     * Check if Power BI error indicates a model load failure
     * Used to distinguish general load errors from capacity-specific issues
     */
    const isPowerBIModelLoadError = (event) => {
        const detail = event?.detail;
        const message = (detail?.message || '') + ' ' + (detail?.detailedMessage || '');
        const messagePatterns = [
            'ExplorationContainer_FailedToLoadModel',
            'FailedToLoadModel',
            'Cannot load model',
            'Couldn\'t load the model schema'
        ];

        return messagePatterns.some(pattern => message.includes(pattern));
    };

    /**
     * Check if Power BI error indicates capacity is paused/inactive
     * Based on actual error captured from console when capacity is paused
     */
    const isPowerBICapacityPauseError = (event) => {
        const detail = event?.detail;
        const message = detail?.message || '';
        const detailedMessage = detail?.detailedMessage || '';
        const technicalDetails = detail?.technicalDetails || {};

        const messageMatch = message.includes('CapacityNotActive') ||
            detailedMessage.includes('CapacityNotActive') ||
            message.includes('Capacity operation failed') ||
            detailedMessage.includes('Capacity operation failed');

        // Pattern 2: Check technicalDetails.errorInfo for CapacityNotActive
        let capacityErrorFound = false;
        if (technicalDetails.errorInfo && Array.isArray(technicalDetails.errorInfo)) {
            capacityErrorFound = technicalDetails.errorInfo.some(info => {
                const value = info.value || '';
                return value.includes('CapacityNotActive') || value.includes('Capacity operation failed');
            });
        }

        // Return true if either pattern matches
        return messageMatch || capacityErrorFound;
    };

    const logActivity = (event, report) => {
        const embeddedReport = report || reportRef.current;
        console.log("PowerBIEmbed event ["+event.type+"]: ", event, embeddedReport);
        if(event.type === 'rendered'){
            const tokenTypeLabel = isEmbedToken === true ? 'Embed' : 'AAD';
            console.log('[PowerBI Render] Token type used:', tokenTypeLabel);
            if (!embeddedReport || typeof embeddedReport.getFilters !== 'function') {
                console.warn('[PowerBI] Embedded report not ready during rendered event; skipping getFilters');
                return;
            }

            embeddedReport.getFilters().then(filters => {
                var payload = { filters: filters.map(filter => ({ operator: filter.operator, ...filter.target, values: filter.values, ...filter.displaySettings })) };
                console.log("PowerBIEmbed " + event.type, payload);
                // PATCH: Wrap PostHog capture in try-catch to prevent unhandled rejections
                try {
                    posthog.capture('PBI-report-event-rendered', payload);
                } catch (posthogError) {
                    console.error('[PostHog] Capture failed (non-critical):', posthogError);
                }
            }).catch(filterError => {
                // PATCH: Catch promise rejection from getFilters()
                console.error('[PowerBI] Failed to get filters for rendered event:', filterError);
            });
        }

        if(event.type === 'pageChanged'){
            var payload = {};
            const newPage = event?.detail?.newPage;
            if (newPage) {
                payload = { ...payload, page: newPage.displayName };
            }

            console.log('PowerBIEmbed ' + event.type, payload);
            // PATCH: Wrap PostHog capture in try-catch to prevent unhandled rejections
            try {
                posthog.capture('PBI-report-event' + event.type, payload);
            } catch (posthogError) {
                console.error('[PostHog] Capture failed (non-critical):', posthogError);
            }

        }
        else if(event.type === 'dataSelected' || event.type === 'visualClicked' || event.type === 'selectionChanged') {
            var payload = {};
            const page = event?.detail?.page?.displayName;
            if (page) {
                payload = { ...payload, page: page };
            }
            const report = event?.detail?.report?.displayName;
            if (report) {
                payload = { ...payload, report: report };
            }
            const visual = event?.detail?.visual?.name;
            if (visual) {
                payload = { ...payload, visual: visual, visual_type: event?.detail?.visual?.type, visual_title: event?.detail?.visual?.title };
            }
            const dataPoints = event?.detail?.dataPoints?.map((dataPoint) => {
                return {
                    value: dataPoint?.identity[0].equals,
                    ...dataPoint?.identity[0].target
                };
            });
            if (dataPoints?.length) {
                payload = { ...payload, dataPoints: dataPoints };
            }
            console.log('PowerBIEmbed ' + event.type, payload);
            // PATCH: Wrap PostHog capture in try-catch to prevent unhandled rejections
            try {
                posthog.capture('PBI-report-event-' + event.type, payload);
            } catch (posthogError) {
                console.error('[PostHog] Capture failed (non-critical):', posthogError);
            }
        }
    };

    // CRITICAL: Handle navigation and initial data fetch
    // This useEffect runs when reportId changes and fetches fresh tokens
    useEffect(() => {
        if (!reportId) return;

        console.log('═══════════════════════════════════════════════════════════════');
        console.log('[PowerBI Navigation] Report ID changed:', reportId);
        console.log('[PowerBI Navigation] Current state:', {
            accessToken: accessToken ? `${accessToken.substring(0, 20)}...` : 'null',
            embedUrl: embedUrl || 'null',
            tokenExpiration: tokenExpiration || 'null',
            isEmbedToken,
            isInitializing
        });

        // CRITICAL FIX: Always fetch fresh tokens on navigation
        // Don't rely on cached tokens from location.state as they may be stale
        // The Sidebar no longer passes tokens, so we always fetch fresh ones

        console.log('[PowerBI Navigation] ✓ Clearing cached state and fetching fresh token');
        console.log('[PowerBI Navigation] Reason: Sidebar no longer passes tokens to prevent stale token usage');

        // Reset state when switching to a new report
        setAccessToken(null);
        setEmbedUrl(null);
        setIsEmbedToken(undefined);
        setTokenExpiration(null);

        // Reset retry counter and refresh flags for new report
        retryCountRef.current = 0;
        isRefreshingRef.current = false;
        setIsRefreshing(false);
        setIsCapacityPaused(false);
        capacityModalShownRef.current = false;

        console.log('[PowerBI Navigation] State reset complete - fetching fresh data...');
        console.log('═══════════════════════════════════════════════════════════════');

        // Immediately fetch fresh token data
        fetchInitialReportData();
    }, [reportId]);

    useEffect(() => {
        // Development: Log report state for debugging (fires during initialization before fetch completes)
        if (process.env.NODE_ENV === 'development') {
            if (!reportId || !embedUrl || !accessToken || isEmbedToken === undefined) {
                console.log('[PowerBI Debug] Initialization in progress - Report state:', {
                    reportId: reportId || 'none',
                    hasEmbedUrl: !!embedUrl,
                    hasAccessToken: !!accessToken,
                    isEmbedToken,
                    tokenExpiration: tokenExpiration || 'none'
                });
            } else {
                console.log('[PowerBI Debug] Report fully initialized:', {
                    reportId,
                    hasEmbedUrl: !!embedUrl,
                    hasAccessToken: !!accessToken,
                    isEmbedToken,
                    tokenExpiration
                });
            }
        }

        // Development: Expose token refresh function for manual testing
        if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
            // Test 1: Full token refresh (already exists)
            window.testPowerBITokenRefresh = async () => {
                console.log('[TEST] Manually triggering PowerBI token refresh...');
                try {
                    await refreshPowerBIToken();
                    console.log('[TEST] Token refresh completed successfully');
                } catch (error) {
                    console.error('[TEST] Token refresh failed:', error);
                }
            };

            // Test 2: Simulate token expiry error
            window.testPowerBITokenExpiry = async () => {
                console.log('[TEST] Simulating PowerBI token expiry error...');

                // Safety guards
                if (!reportId) {
                    console.error('[TEST] Cannot test: No reportId available. Please load a report first.');
                    return;
                }

                if (isRefreshing) {
                    console.warn('[TEST] Cannot test: Token refresh already in progress.');
                    return;
                }

                const mockEvent = {
                    type: 'error',
                    detail: {
                        message: 'TokenExpired: The access token has expired',
                        errorCode: 'TokenExpired',
                        detailedMessage: 'Get report failed - Token has expired'
                    }
                };
                console.log('[TEST] Triggering error detection with mock event:', mockEvent);
                if (isPowerBITokenError(mockEvent)) {
                    console.log('[TEST] Token error detected, triggering refresh...');
                    try {
                        await refreshPowerBIToken();
                        console.log('[TEST] ✓ Token refresh completed successfully');
                    } catch (error) {
                        console.error('[TEST] ✗ Token refresh failed:', error.message);
                    }
                } else {
                    console.warn('[TEST] Token error NOT detected - check isPowerBITokenError logic');
                }
            };

            // Test 3: Trigger various PowerBI errors
            window.triggerPowerBIError = async (errorType = 'token') => {
                console.log(`[TEST] Triggering PowerBI error type: ${errorType}`);

                // Safety guards
                if (!reportId) {
                    console.error('[TEST] Cannot test: No reportId available. Please load a report first.');
                    return;
                }

                if (isRefreshing) {
                    console.warn('[TEST] Cannot test: Token refresh already in progress.');
                    return;
                }

                const mockEvents = {
                    token: {
                        type: 'error',
                        detail: { message: 'Get report failed', errorCode: '401' }
                    },
                    network: {
                        type: 'error',
                        detail: { message: 'Network error occurred', errorCode: 'NetworkError' }
                    },
                    permission: {
                        type: 'error',
                        detail: { message: 'Unauthorized access', errorCode: '403' }
                    }
                };
                const mockEvent = mockEvents[errorType] || mockEvents.token;
                console.log('[TEST] Mock event:', mockEvent);
                if (isPowerBITokenError(mockEvent)) {
                    console.log('[TEST] Token error detected, triggering refresh...');
                    try {
                        await refreshPowerBIToken();
                        console.log('[TEST] ✓ Token refresh completed successfully');
                    } catch (error) {
                        console.error('[TEST] ✗ Token refresh failed:', error.message);
                    }
                } else {
                    console.log('[TEST] Not a token error, would show generic error modal');
                }
            };

            // Test 4: Clear PowerBI token state
            window.clearPowerBIToken = () => {
                console.log('[TEST] Clearing PowerBI token state...');
                console.log('[TEST] Before clear:', { accessToken, embedUrl, isEmbedToken });
                setAccessToken('');
                setEmbedUrl('');
                console.log('[TEST] Token state cleared. Report will fail to load until refreshed.');
                console.log('[TEST] Use window.testPowerBITokenRefresh() to recover.');
            };

            // Test 5: Get current token info
            window.getPowerBITokenInfo = () => {
                const now = new Date();
                const expiryDate = tokenExpiration ? new Date(tokenExpiration) : null;
                const timeUntilExpiry = expiryDate ? expiryDate.getTime() - now.getTime() : null;

                const info = {
                    reportId,
                    hasAccessToken: !!accessToken,
                    tokenLength: accessToken?.length || 0,
                    embedUrl,
                    isEmbedToken,
                    tokenExpiration,
                    expiresAt: expiryDate?.toISOString() || 'unknown',
                    minutesUntilExpiry: timeUntilExpiry ? Math.round(timeUntilExpiry / 1000 / 60) : null,
                    hasScheduledRefresh: !!refreshTimerRef.current,
                    isRefreshing,
                    retryCount: retryCountRef.current,
                    maxRetries: MAX_RETRY_ATTEMPTS
                };
                console.log('[TEST] PowerBI Token Info:', info);
                return info;
            };

            // Test 6: Test proactive token refresh
            window.testProactiveRefresh = async () => {
                console.log('[TEST] Testing proactive token refresh...');
                try {
                    await refreshPowerBIToken(true);
                    console.log('[TEST] ✓ Proactive refresh completed');
                } catch (error) {
                    console.error('[TEST] ✗ Proactive refresh failed:', error);
                }
            };

        }

        // Cleanup on unmount
        return () => {
            console.log('[PowerBI] Component unmounting, cleaning up...');
            // Reset refresh flags to prevent state updates on unmounted component
            isRefreshingRef.current = false;
            setIsRefreshing(false);
            // Clear any scheduled token refresh timers
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [reportId, embedUrl, accessToken, isEmbedToken]);

    // MICROSOFT RECOMMENDED: Schedule proactive token refresh when token expiration is known
    // - Monitors token expiration every 30 seconds
    // - Refreshes 10 minutes before expiration
    // - Uses setAccessToken() for seamless refresh without page reload
    useEffect(() => {
        if (tokenExpiration && reportRef.current && !isInitializing) {
            console.log('[PowerBI] Token expiration updated, scheduling proactive refresh');
            scheduleTokenRefresh();
        }

        // Cleanup timer when component unmounts or token expiration changes
        return () => {
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [tokenExpiration, reportRef.current, isInitializing]);

    // Keep report fit synced with container width/height changes (e.g., sidebar collapse/expand)
    useEffect(() => {
        if (isInitializing || !embedContainerRef.current) {
            return;
        }

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', applyFitToContainerLayout);
            return () => {
                window.removeEventListener('resize', applyFitToContainerLayout);
            };
        }

        let resizeDebounceTimer = null;
        const triggerFitUpdate = () => {
            if (resizeDebounceTimer) {
                clearTimeout(resizeDebounceTimer);
            }
            resizeDebounceTimer = setTimeout(() => {
                applyFitToContainerLayout();
            }, 120);
        };

        const observer = new ResizeObserver(triggerFitUpdate);
        observer.observe(embedContainerRef.current);
        window.addEventListener('resize', triggerFitUpdate);

        return () => {
            if (resizeDebounceTimer) {
                clearTimeout(resizeDebounceTimer);
            }
            observer.disconnect();
            window.removeEventListener('resize', triggerFitUpdate);
        };
    }, [isInitializing, reportId]);

    // Check module access before rendering
    const moduleAccess = hasModuleAccess();

    // If user doesn't have access to the module, show access denied
    if (moduleAccess === false) {
        return (
            <div className="flex flex-col w-[100%] gap-5">
                <TitleComponent title={"Access Denied"} />
                <p>You do not have access to this report module.</p>
            </div>
        );
    }

    // If still loading user details, show loading state
    if (moduleAccess === undefined) {
        return (
            <div className="flex flex-col w-[100%] gap-5">
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div className='flex flex-col w-full gap-5'>
            {isInitializing ? (
                <div className='w-full h-[80vh] flex justify-center items-center'>
                    <Spin size="large" tip="Loading report..." />
                </div>
            ) : isCapacityPaused ? (
                <div className='w-full h-[80vh] flex justify-center items-center'>
                    <p className='text-gray-500'>The Reporting service is currently unavailable.</p>
                </div>
            ) : (
                <div ref={embedContainerRef} className='w-full mt-[28px] flex justify-center items-end'>
                    <PowerBIEmbed
                    embedConfig={{
                        type: 'report',
                        id: reportId,
                        embedUrl: embedUrl,
                        accessToken: accessToken,
                        tokenType: isEmbedToken === true ? models.TokenType.Embed : models.TokenType.Aad,
                        settings: {
                            panes: {
                                filters: {
                                    expanded: false,
                                    visible: true
                                }
                            },
                            background: models.BackgroundType.Transparent,
                            layoutType: models.LayoutType.Custom,
                            customLayout: {
                                displayOption: models.DisplayOption.FitToPage
                            },
                        }
                    }}
                    eventHandlers={new Map([
                        ['loaded', function (event, entity) {
                            console.log("PowerBIEmbed event [loaded]: ", event, entity); 
                            applyFitToContainerLayout(entity);
                            logActivity(event, entity);
                        }],
                        ['saved', function (event, entity) { 
                            console.log("PowerBIEmbed event [saved]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['rendered', function (event, entity) {
                            applyFitToContainerLayout(entity);
                            logActivity(event, entity);
                        }],
                        ['saveAsTriggered', function (event, entity) { 
                            console.log("PowerBIEmbed event [saveAsTriggered]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['error', async function (event, entity) {
                            // PATCH: Wrap entire async handler in try-catch to prevent unhandled promise rejections in production
                            try {
                                console.log("PowerBIEmbed event [error]: ", event, entity);
                                logActivity(event);
                                const tokenTypeLabel = isEmbedToken === true ? 'Embed' : 'AAD';

                                // ENHANCED LOGGING: Capture full error details for capacity pause detection
                                console.log('========== PowerBI ERROR DETAILS ==========');
                                console.log('Event Type:', event?.type);
                                console.log('Error Message:', event?.detail?.message);
                                console.log('Error Code:', event?.detail?.errorCode);
                                console.log('Detailed Message:', event?.detail?.detailedMessage);
                                console.log('Level:', event?.detail?.level);
                                console.log('Technical Details:', event?.detail?.technicalDetails);
                                console.log('Request ID:', event?.detail?.requestId);
                                console.log('Timestamp:', event?.detail?.timestamp);
                                console.log('Token Type:', tokenTypeLabel);
                                console.log('Full Detail Object:', JSON.stringify(event?.detail, null, 2));
                                console.log('==========================================');

                                // PRIORITY 1: Check if this is a capacity pause error (most specific)
                                if (isPowerBICapacityPauseError(event)) {
                                    console.error('[PowerBI] Capacity pause/inactive detected');
                                    console.error('[PowerBI] Token type at error:', tokenTypeLabel);

                                    // PATCH: Wrap PostHog capture in try-catch to prevent failures
                                    try {
                                        posthog.capture('PBI-capacity-paused', {
                                            reportId,
                                            errorMessage: event?.detail?.message,
                                            detailedMessage: event?.detail?.detailedMessage
                                        });
                                    } catch (posthogError) {
                                        console.error('[PowerBI] PostHog capture failed (non-critical):', posthogError);
                                    }

                                    showCapacityUnavailableModal();

                                    return; // Stop processing, don't attempt token refresh
                                }

                            // PRIORITY 2: Check if this is a token expiration error
                            if (isPowerBITokenError(event)) {
                                console.log('[PowerBI] Token expiration detected, attempting refresh...');

                                try {
                                    // Refresh the token
                                    const newTokenData = await refreshPowerBIToken();

                                    if (newTokenData && entity) {
                                        console.log('[PowerBI] Applying new token to embedded report...');

                                        // CRITICAL FIX: Explicitly call setAccessToken on the embed instance
                                        // Simply updating React state is NOT enough - the iframe needs to be told to use the new token
                                        try {
                                            await entity.setAccessToken(newTokenData.token);
                                            console.log('[PowerBI] ✓ New token applied successfully via setAccessToken()');

                                            // PATCH: Wrap PostHog capture in try-catch
                                            try {
                                                posthog.capture('PBI-token-applied-to-iframe', {
                                                    reportId,
                                                    tokenType: newTokenData.isEmbedToken ? 'Embed' : 'AAD'
                                                });
                                            } catch (posthogError) {
                                                console.error('[PowerBI] PostHog capture failed (non-critical):', posthogError);
                                            }
                                        } catch (setTokenError) {
                                            console.error('[PowerBI] ✗ Failed to apply token to iframe:', setTokenError);

                                            // PATCH: Wrap PostHog capture in try-catch
                                            try {
                                                posthog.capture('PBI-token-application-failed', {
                                                    reportId,
                                                    error: setTokenError.message
                                                });
                                            } catch (posthogError) {
                                                console.error('[PowerBI] PostHog capture failed (non-critical):', posthogError);
                                            }

                                            // PATCH: Wrap Modal in try-catch
                                            try {
                                                Modal.error({
                                                    title: 'Token Update Failed',
                                                    content: 'Failed to apply the new authentication token to the report. Please refresh the page to reload the report.',
                                                    okText: 'Refresh Page',
                                                    onOk: () => window.location.reload()
                                                });
                                            } catch (modalError) {
                                                console.error('[PowerBI] Modal display failed:', modalError);
                                                if (window.confirm('Token Update Failed\n\nFailed to apply the new authentication token to the report. Click OK to refresh the page.')) {
                                                    window.location.reload();
                                                }
                                            }
                                        }
                                    }
                                } catch (error) {
                                    console.error('[PowerBI] Failed to recover from token expiration:', error);
                                    // Error modal already shown by refreshPowerBIToken
                                }
                            } else if (isPowerBIModelLoadError(event)) {
                                console.warn('[PowerBI] Model load error detected (non-capacity)');
                                console.warn('[PowerBI] Token type at error:', tokenTypeLabel);

                                if (isEmbedToken === false) {
                                    console.warn('[PowerBI] Attempting embed token refresh after model load error on AAD token');
                                    try {
                                        await refreshPowerBIToken();
                                    } catch (error) {
                                        console.error('[PowerBI] Embed token refresh failed after model load error:', error);
                                    }
                                }
                            } else {
                                // Not a token error or capacity pause - log for analysis
                                console.warn('[PowerBI] Non-token, non-capacity error detected');
                                console.warn('[PowerBI] Error will be shown by PowerBI SDK default error UI');
                            }
                            } catch (unexpectedError) {
                                // PATCH: Catch any unexpected errors in the error handler itself
                                console.error('[PowerBI] Critical: Unexpected error in PowerBI error handler:', unexpectedError);
                                console.error('[PowerBI] Original event:', event);

                                // Last resort: Show a generic error message
                                try {
                                    Modal.error({
                                        title: 'Report Error',
                                        content: 'An unexpected error occurred while handling a report error. Please refresh the page.',
                                        okText: 'Refresh Page',
                                        onOk: () => window.location.reload()
                                    });
                                } catch (finalModalError) {
                                    console.error('[PowerBI] Even fallback modal failed:', finalModalError);
                                    if (window.confirm('Report Error\n\nAn unexpected error occurred. Click OK to refresh the page.')) {
                                        window.location.reload();
                                    }
                                }
                            }
                        }],
                        ['dataSelected', function (event, entity) {
                            console.log("PowerBIEmbed event [dataSelected]: ", event, entity);  
                            logActivity(event);
                        }],
                        ['buttonClicked', function (event, entity) { 
                            console.log("PowerBIEmbed event [buttonClicked]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['info', function (event, entity) { 
                            console.log("PowerBIEmbed event [info]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['filtersApplied', function (event, entity) { 
                            console.log("PowerBIEmbed event [filtersApplied]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['pageChanged', function (event, entity) { 
                            console.log("PowerBIEmbed event [pageChanged]: ", event, entity); 
                            applyFitToContainerLayout(entity);
                            logActivity(event, entity);
                        }],
                        ['commandTriggered', function (event, entity) { 
                            console.log("PowerBIEmbed event [commandTriggered]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['swipeStart', function (event, entity) { 
                            console.log("PowerBIEmbed event [swipeStart]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['swipeEnd', function (event, entity) { 
                            console.log("PowerBIEmbed event [swipeEnd]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['bookmarkApplied', function (event, entity) { 
                            console.log("PowerBIEmbed event [bookmarkApplied]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['dataHyperlinkClicked', function (event, entity) { 
                            console.log("PowerBIEmbed event [dataHyperlinkClicked]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['visualRendered', function (event, entity) { 
                            console.log("PowerBIEmbed event [visualRendered]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['visualClicked', function (event, entity) { 
                            console.log("PowerBIEmbed event [visualClicked]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['selectionChanged', function (event, entity) { 
                            console.log("PowerBIEmbed event [selectionChanged]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['renderingStarted', function (event, entity) { 
                            console.log("PowerBIEmbed event [renderingStarted]: ", event, entity); 
                            logActivity(event);
                        }],
                        ['blur', function (event, entity) { 
                            console.log("PowerBIEmbed event [blur]: ", event, entity); 
                            logActivity(event);
                        }],
                    ])}
                    cssClassName="pbi-report-embed"
                    getEmbeddedComponent={(embeddedReport) => {
                        console.log('Embedded report object:', embeddedReport);
                        // Store report reference for proactive token refresh
                        reportRef.current = embeddedReport;
                        applyFitToContainerLayout(embeddedReport);
                    }}
                />
                </div>
            )}
        </div>
    );
}




export default Report;
